import { useModel as _useModel, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-6 print:hidden" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-4" }
const _hoisted_3 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_4 = {
  key: 0,
  class: "py-10 text-center text-gray-500 space-y-4 print:hidden"
}
const _hoisted_5 = {
  key: 1,
  class: "py-10 flex justify-center print:hidden"
}
const _hoisted_6 = {
  key: 2,
  class: "py-10 text-center text-gray-500 print:hidden"
}
const _hoisted_7 = {
  key: 3,
  class: "count-sheet max-h-[60vh] overflow-auto rounded-lg border border-gray-200 print:max-h-none print:overflow-visible print:border-0 print:rounded-none"
}
const _hoisted_8 = {
  key: 0,
  class: "count-sheet-page p-6 space-y-4 border-b border-gray-200 last:border-b-0"
}
const _hoisted_9 = { class: "flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1" }
const _hoisted_10 = { class: "text-xl font-semibold" }
const _hoisted_11 = { class: "text-lg" }
const _hoisted_12 = {
  key: 0,
  class: "text-gray-500"
}
const _hoisted_13 = { class: "text-gray-600" }
const _hoisted_14 = { class: "w-full border-collapse text-normal" }
const _hoisted_15 = { class: "text-left" }
const _hoisted_16 = { class: "border border-gray-400 px-2 py-1.5 font-semibold" }
const _hoisted_17 = { class: "border border-gray-400 px-2 py-1.5 font-semibold" }
const _hoisted_18 = {
  key: 0,
  class: "border border-gray-400 px-2 py-1.5 text-right font-semibold"
}
const _hoisted_19 = { class: "border border-gray-400 px-2 py-1.5 font-semibold w-32" }
const _hoisted_20 = { class: "border border-gray-400 px-2 py-1.5 font-semibold w-56" }
const _hoisted_21 = { class: "border border-gray-400 px-2 py-1.5" }
const _hoisted_22 = { class: "border border-gray-400 px-2 py-1.5" }
const _hoisted_23 = {
  key: 0,
  class: "border border-gray-400 px-2 py-1.5 text-right"
}
const _hoisted_24 = { class: "flex gap-x-12 pt-10" }
const _hoisted_25 = { class: "flex-1 border-t border-gray-500 pt-1 text-gray-600" }
const _hoisted_26 = { class: "w-48 border-t border-gray-500 pt-1 text-gray-600" }

import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { Printer } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Spinner from '@/components/ui/Spinner.vue';
import { drugUnitValue, formatQuantity } from '@/composables/inventory/inventoryTable';
import { useInventoryScope } from '@/composables/inventory/useInventoryScope';
import { useMedicineCountSheet } from '@/plugins/store/actions/queries/inventory';

const PRINTING_CLASS = 'count-sheet-printing';


export default /*@__PURE__*/_defineComponent({
  __name: 'CountSheetDialog',
  props: {
    "open": { type: Boolean, ...{ required: true } },
    "openModifiers": {},
  },
  emits: ["update:open"],
  setup(__props) {

const isOpen = _useModel<boolean>(__props, 'open');

const { scope } = useInventoryScope();
const locationIds = computed(() => scope.value.locationIds);
const { data, isFetching, isError, refetch } = useMedicineCountSheet(locationIds, { enabled: computed(() => isOpen.value && locationIds.value.length > 0) });

const farms = computed(() => data.value ?? []);
const hasRows = computed(() => farms.value.some((farm) => farm.rows.length > 0));

/** Off prints a blind sheet: the counter writes down what is on the shelf without seeing what the ledger expects. */
const showCalculated = ref(true);
const today = new Date();

/** Marks the page as being printed, so the print stylesheet below can reduce it to this dialog's sheet. */
function stopPrinting() {
    document.body.classList.remove(PRINTING_CLASS);
}

async function print() {
    document.body.classList.add(PRINTING_CLASS);
    window.addEventListener('afterprint', stopPrinting, { once: true });

    await nextTick();
    window.print();
}

onBeforeUnmount(() => {
    window.removeEventListener('afterprint', stopPrinting);
    stopPrinting();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isOpen).value = $event)),
    class: "count-sheet-dialog sm:max-w-[900px]"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(DialogHeader), null, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogTitle), null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.countSheet.title')), 1)
              ]),
              _: 1
            }),
            _createVNode(_unref(DialogDescription), { class: "mt-2 text-gray-500" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.countSheet.description')), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", _hoisted_3, [
            _createVNode(_unref(Checkbox), {
              checked: showCalculated.value,
              "onUpdate:checked": _cache[0] || (_cache[0] = ($event: any) => ((showCalculated).value = $event))
            }, null, 8, ["checked"]),
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.showCalculated')), 1)
          ]),
          _createVNode(_unref(Button), {
            disabled: !hasRows.value || _unref(isFetching),
            onClick: print
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Printer), {
                class: "size-4",
                "aria-hidden": "true"
              }),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('views.health.inventory.buttons.print')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ]),
      (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", null, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
            _createVNode(_unref(Button), {
              variant: "outline",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(refetch)()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
              ]),
              _: 1
            })
          ]))
        : (_unref(isFetching) && farms.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(Spinner, { class: "size-6 animate-spin" })
            ]))
          : (!hasRows.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.empty')), 1))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farms.value, (farm) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: farm.farm_id
                  }, [
                    (farm.rows.length)
                      ? (_openBlock(), _createElementBlock("section", _hoisted_8, [
                          _createElementVNode("header", _hoisted_9, [
                            _createElementVNode("div", null, [
                              _createElementVNode("h2", _hoisted_10, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.sheetHeading')), 1),
                              _createElementVNode("div", _hoisted_11, [
                                _createTextVNode(_toDisplayString(farm.farm_name) + " ", 1),
                                (farm.farm_number)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_12, "(" + _toDisplayString(_ctx.$t('views.health.inventory.countSheet.farmNumber')) + " " + _toDisplayString(farm.farm_number) + ")", 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("div", _hoisted_13, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.printed')) + " " + _toDisplayString(_ctx.$d(_unref(today), 'short')), 1)
                          ]),
                          _createElementVNode("table", _hoisted_14, [
                            _createElementVNode("thead", null, [
                              _createElementVNode("tr", _hoisted_15, [
                                _createElementVNode("th", _hoisted_16, _toDisplayString(_ctx.$t('views.health.inventory.table.headings.drug')), 1),
                                _createElementVNode("th", _hoisted_17, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.unit')), 1),
                                (showCalculated.value)
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_18, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.calculated')), 1))
                                  : _createCommentVNode("", true),
                                _createElementVNode("th", _hoisted_19, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.counted')), 1),
                                _createElementVNode("th", _hoisted_20, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.remark')), 1)
                              ])
                            ]),
                            _createElementVNode("tbody", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farm.rows, (row) => {
                                return (_openBlock(), _createElementBlock("tr", {
                                  key: _unref(drugUnitValue)(row),
                                  class: "h-10 break-inside-avoid"
                                }, [
                                  _createElementVNode("td", _hoisted_21, _toDisplayString(row.drug_name), 1),
                                  _createElementVNode("td", _hoisted_22, _toDisplayString(row.unit_name), 1),
                                  (showCalculated.value)
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_23, _toDisplayString(_unref(formatQuantity)(row.calculated)), 1))
                                    : _createCommentVNode("", true),
                                  _cache[3] || (_cache[3] = _createElementVNode("td", { class: "border border-gray-400 px-2 py-1.5" }, null, -1)),
                                  _cache[4] || (_cache[4] = _createElementVNode("td", { class: "border border-gray-400 px-2 py-1.5" }, null, -1))
                                ]))
                              }), 128))
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_24, [
                            _createElementVNode("div", _hoisted_25, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.countedBy')), 1),
                            _createElementVNode("div", _hoisted_26, _toDisplayString(_ctx.$t('views.health.inventory.countSheet.date')), 1)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ]))
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})